// CfbReader.ts - Compound Binary File (OLE2) container parser
//
// Parses Microsoft Compound Binary File Format (also called OLE2/COM structured
// storage). This is the container format used by .xls, .doc, .ppt, etc.
//
// Reference: [MS-CFB] https://docs.microsoft.com/en-us/openspecs/windows_protocols/ms-cfb
//
// Overview:
// - The file is divided into fixed-size sectors.
// - A FAT (File Allocation Table) maps sector chains (like a linked list).
// - A directory stream lists entries (root, streams, storages).
// - Each stream's data is read by following its FAT chain.
// - Small streams (< mini-stream cutoff) are stored in the mini-stream
//   and tracked by a separate mini-FAT.

/** A single directory entry in the CFB. */
export interface CfbDirectoryEntry {
  name: string;
  type: number; // 0=unknown, 1=storage, 2=stream, 5=root
  startSector: number;
  size: number;
  /** Child entries (populated after building the tree). */
  children?: CfbDirectoryEntry[];
}

/** Parsed CFB container. */
export interface CfbContainer {
  entries: CfbDirectoryEntry[];
  /** Read a stream by entry name (case-insensitive search). */
  getStream(name: string): Uint8Array | null;
  /** Read a stream by directory entry. */
  getStreamByEntry(entry: CfbDirectoryEntry): Uint8Array;
}

// CFB magic signature: D0 CF 11 E0 A1 B1 1A E1
const CFB_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

// End-of-chain / free sector markers
const ENDOFCHAIN = 0xfffffffe; // -2 as unsigned 32-bit
const FREESECT = 0xffffffff; // -1 as unsigned 32-bit

/**
 * Check whether the given buffer starts with the CFB magic signature.
 */
export function isCfbFile(data: ArrayBuffer | Uint8Array): boolean {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data, 0, 8);
  if (bytes.length < 8) return false;
  for (let i = 0; i < 8; i++) {
    if (bytes[i] !== CFB_SIGNATURE[i]) return false;
  }
  return true;
}

/**
 * Parse a Compound Binary File from raw bytes.
 */
export function parseCfb(data: ArrayBuffer): CfbContainer {
  const view = new DataView(data);
  const buf = new Uint8Array(data);

  // ---- Header (512 bytes) ----

  // Validate signature
  for (let i = 0; i < 8; i++) {
    if (buf[i] !== CFB_SIGNATURE[i]) {
      throw new Error('Not a valid CFB file (bad signature)');
    }
  }

  // Sector size: 2^sectorShift
  const sectorShift = view.getUint16(30, true);
  const sectorSize = 1 << sectorShift; // 512 for v3, 4096 for v4

  // Mini-sector size: 2^miniSectorShift (always 64)
  const miniSectorShift = view.getUint16(32, true);
  const miniSectorSize = 1 << miniSectorShift;

  // Mini-stream cutoff size (typically 4096)
  const miniStreamCutoff = view.getUint32(56, true);

  // Total FAT sectors (for reference)
  // const fatSectors = view.getUint32(44, true);

  // First directory sector SECT
  const firstDirSector = view.getUint32(48, true);

  // First mini-FAT sector SECT
  const firstMiniFatSector = view.getInt32(60, true);
  // Number of mini-FAT sectors
  // const miniFatSectorCount = view.getUint32(64, true);

  // First DIFAT sector SECT
  const firstDifatSector = view.getInt32(68, true);
  // Number of DIFAT sectors
  const difatSectorCount = view.getUint32(72, true);

  // ---- Build FAT ----

  // Collect all FAT sector numbers from the header DIFAT array (109 entries)
  // and any additional DIFAT sectors.
  const fatSectorNumbers: number[] = [];

  // 109 DIFAT entries start at offset 76 in the header
  for (let i = 0; i < 109; i++) {
    const sect = view.getUint32(76 + i * 4, true);
    if (sect === FREESECT || sect === ENDOFCHAIN) break;
    fatSectorNumbers.push(sect);
  }

  // Follow DIFAT chain for additional FAT sector entries
  if (difatSectorCount > 0 && firstDifatSector >= 0 && firstDifatSector !== (ENDOFCHAIN | 0)) {
    let difatSect = firstDifatSector;
    let safety = 0;
    while (difatSect >= 0 && (difatSect >>> 0) < ENDOFCHAIN && safety < 1000) {
      const offset = sectorOffset(difatSect);
      const entriesPerSect = (sectorSize / 4) - 1; // last 4 bytes = next DIFAT sector
      for (let i = 0; i < entriesPerSect; i++) {
        const sect = view.getUint32(offset + i * 4, true);
        if (sect === FREESECT || sect === ENDOFCHAIN) break;
        fatSectorNumbers.push(sect);
      }
      difatSect = view.getUint32(offset + sectorSize - 4, true);
      safety++;
    }
  }

  // Build the full FAT array (each entry is a uint32 next-sector pointer)
  const totalFatEntries = fatSectorNumbers.length * (sectorSize / 4);
  const fat = new Int32Array(totalFatEntries);
  for (let si = 0; si < fatSectorNumbers.length; si++) {
    const offset = sectorOffset(fatSectorNumbers[si]);
    const base = si * (sectorSize / 4);
    for (let i = 0; i < sectorSize / 4; i++) {
      fat[base + i] = view.getInt32(offset + i * 4, true);
    }
  }

  // ---- Helper: sector offset in the file ----

  function sectorOffset(sect: number): number {
    // Sectors are after the 512-byte header. For v4 files with 4096-byte
    // sectors the header is padded to one full sector, but the formula
    // (sect + 1) * sectorSize works for both since the header occupies
    // sector -1 conceptually.
    return (sect + 1) * sectorSize;
  }

  // ---- Helper: read a sector chain into a single buffer ----

  function readChain(startSect: number, totalSize?: number): Uint8Array {
    const sectors: number[] = [];
    let sect = startSect;
    let safety = 0;
    const maxSectors = Math.ceil(data.byteLength / sectorSize);
    while (sect >= 0 && (sect >>> 0) < ENDOFCHAIN && safety < maxSectors) {
      sectors.push(sect);
      sect = fat[sect];
      safety++;
    }
    const rawLen = sectors.length * sectorSize;
    const result = new Uint8Array(totalSize != null ? Math.min(totalSize, rawLen) : rawLen);
    let pos = 0;
    for (const s of sectors) {
      const off = sectorOffset(s);
      const copyLen = Math.min(sectorSize, result.length - pos);
      if (copyLen <= 0) break;
      result.set(buf.subarray(off, off + copyLen), pos);
      pos += copyLen;
    }
    return result;
  }

  // ---- Parse directory entries ----

  const dirData = readChain(firstDirSector);
  const dirView = new DataView(dirData.buffer, dirData.byteOffset, dirData.byteLength);
  const entryCount = Math.floor(dirData.length / 128);
  const entries: CfbDirectoryEntry[] = [];

  for (let i = 0; i < entryCount; i++) {
    const base = i * 128;
    // Entry name: UTF-16LE, up to 64 bytes (32 chars)
    const nameSize = dirView.getUint16(base + 64, true);
    const nameBytes = Math.max(0, nameSize - 2); // exclude null terminator
    let name = '';
    for (let j = 0; j < nameBytes; j += 2) {
      const code = dirView.getUint16(base + j, true);
      if (code === 0) break;
      name += String.fromCharCode(code);
    }

    const type = dirData[base + 66];
    if (type === 0 && i > 0) continue; // empty/unused entry

    const startSector = dirView.getInt32(base + 116, true);
    // Size field: 4 bytes at offset 120 for v3, 8 bytes for v4 (we use 4 bytes)
    const size = dirView.getUint32(base + 120, true);

    entries.push({ name, type, startSector, size });
  }

  // ---- Build mini-FAT (for small streams) ----

  let miniFat: Int32Array = new Int32Array(0);
  if (firstMiniFatSector >= 0 && (firstMiniFatSector >>> 0) < ENDOFCHAIN) {
    const miniFatData = readChain(firstMiniFatSector);
    miniFat = new Int32Array(miniFatData.buffer, miniFatData.byteOffset, Math.floor(miniFatData.byteLength / 4));
  }

  // ---- Get the mini-stream data (stored in root entry's chain) ----

  let miniStreamData: Uint8Array = new Uint8Array(0);
  const root = entries[0]; // Root entry is always first
  if (root && root.startSector >= 0 && root.size > 0) {
    miniStreamData = readChain(root.startSector, root.size);
  }

  // ---- Helper: read a mini-stream chain ----

  function readMiniChain(startSect: number, totalSize: number): Uint8Array {
    const result = new Uint8Array(totalSize);
    let sect = startSect;
    let pos = 0;
    let safety = 0;
    const maxMini = miniStreamData.length / miniSectorSize;
    while (sect >= 0 && (sect >>> 0) < ENDOFCHAIN && safety < maxMini + 1) {
      const off = sect * miniSectorSize;
      const copyLen = Math.min(miniSectorSize, totalSize - pos);
      if (copyLen <= 0) break;
      result.set(miniStreamData.subarray(off, off + copyLen), pos);
      pos += copyLen;
      sect = miniFat[sect];
      safety++;
    }
    return result;
  }

  // ---- Stream reading interface ----

  function getStreamByEntry(entry: CfbDirectoryEntry): Uint8Array {
    if (entry.size === 0) return new Uint8Array(0);
    if (entry.size < miniStreamCutoff && entry.type !== 5 /* not root */) {
      return readMiniChain(entry.startSector, entry.size);
    }
    return readChain(entry.startSector, entry.size);
  }

  function getStream(name: string): Uint8Array | null {
    const lower = name.toLowerCase();
    const entry = entries.find((e) => e.name.toLowerCase() === lower);
    if (!entry) return null;
    return getStreamByEntry(entry);
  }

  return { entries, getStream, getStreamByEntry };
}
